import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import { FiSave, FiEye, FiZap, FiUpload, FiX, FiPlus, FiMinus } from 'react-icons/fi';
import './CreateQuestion.css';

const CreateQuestion = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { showNotification } = useNotification();
  
  // Form state
  const [formData, setFormData] = useState({
    questionText: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    subject: '',
    examType: '',
    difficulty: 'medium',
    topics: [],
    timeLimit: 60,
    points: 1,
    tags: [],
    media: null,
    hints: [''],
    references: [''],
    status: 'active'
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Options for dropdowns
  const [subjects, setSubjects] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);
  
  // Expert Intelligence Assistant state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);

  // Fetch options on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/questions/creation-options');
        const { subjects, examTypes, topics } = response.data;
        
        setSubjects(subjects);
        setExamTypes(examTypes);
        setAvailableTopics(topics);
      } catch (err) {
        console.error('Error fetching options:', err);
        showNotification('Failed to load form options', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [api, showNotification]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle option changes
  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    handleChange('options', newOptions);
  };

  // Add/remove options
  const addOption = () => {
    if (formData.options.length < 6) {
      handleChange('options', [...formData.options, '']);
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      handleChange('options', newOptions);
      
      // Adjust correct answer if necessary
      if (formData.correctAnswer >= newOptions.length) {
        handleChange('correctAnswer', newOptions.length - 1);
      }
    }
  };

  // Handle array field changes (topics, tags, hints, references)
  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    handleChange(field, newArray);
  };

  const addArrayItem = (field) => {
    handleChange(field, [...formData[field], '']);
  };

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1) {
      const newArray = formData[field].filter((_, i) => i !== index);
      handleChange(field, newArray);
    }
  };

  // Handle topic selection
  const handleTopicToggle = (topic) => {
    const currentTopics = formData.topics;
    if (currentTopics.includes(topic)) {
      handleChange('topics', currentTopics.filter(t => t !== topic));
    } else {
      handleChange('topics', [...currentTopics, topic]);
    }
  };

  // Handle tag input
  const handleTagInput = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = e.target.value.trim();
      if (tag && !formData.tags.includes(tag)) {
        handleChange('tags', [...formData.tags, tag]);
        e.target.value = '';
      }
    }
  };

  const removeTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'audio/mp3'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('Invalid file type. Please upload an image, video, or audio file.', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      showNotification('File size too large. Please upload a file smaller than 10MB.', 'error');
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('type', 'question-media');

      const response = await api.post('/uploads/single', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      handleChange('media', response.data.file);
      showNotification('File uploaded successfully', 'success');
    } catch (err) {
      console.error('File upload error:', err);
      showNotification(err.response?.data?.message || 'File upload failed', 'error');
    }
  };

  // Expert Intelligence Assistant functions
  const generateAISuggestions = async () => {
    if (!aiPrompt.trim()) {
      showNotification('Please enter a prompt for AI assistance', 'warning');
      return;
    }

    try {
      setAiGenerating(true);
      const response = await api.post('/questions/ai-generate', {
        prompt: aiPrompt,
        subject: formData.subject,
        examType: formData.examType,
        difficulty: formData.difficulty,
        context: {
          existingQuestion: formData.questionText,
          topics: formData.topics
        }
      });

      setAiSuggestions(response.data.suggestions);
    } catch (err) {
      console.error('AI generation error:', err);
      showNotification(err.response?.data?.message || 'AI generation failed', 'error');
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAISuggestion = (suggestion) => {
    if (suggestion.questionText) {
      handleChange('questionText', suggestion.questionText);
    }
    if (suggestion.options) {
      handleChange('options', suggestion.options);
    }
    if (suggestion.correctAnswer !== undefined) {
      handleChange('correctAnswer', suggestion.correctAnswer);
    }
    if (suggestion.explanation) {
      handleChange('explanation', suggestion.explanation);
    }
    if (suggestion.topics) {
      handleChange('topics', suggestion.topics);
    }
    if (suggestion.difficulty) {
      handleChange('difficulty', suggestion.difficulty);
    }
    
    setShowAIAssistant(false);
    showNotification('AI suggestions applied successfully', 'success');
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.questionText.trim()) {
      newErrors.questionText = 'Question text is required';
    }

    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.examType) {
      newErrors.examType = 'Exam type is required';
    }

    const validOptions = formData.options.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      newErrors.options = 'At least 2 options are required';
    }

    if (formData.correctAnswer >= validOptions.length) {
      newErrors.correctAnswer = 'Please select a valid correct answer';
    }

    if (!formData.options[formData.correctAnswer]?.trim()) {
      newErrors.correctAnswer = 'Correct answer option cannot be empty';
    }

    if (formData.timeLimit < 10 || formData.timeLimit > 600) {
      newErrors.timeLimit = 'Time limit must be between 10 and 600 seconds';
    }

    if (formData.points < 0.5 || formData.points > 10) {
      newErrors.points = 'Points must be between 0.5 and 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save question
  const handleSave = async (isDraft = false) => {
    if (!isDraft && !validateForm()) {
      showNotification('Please fix the errors before saving', 'error');
      return;
    }

    try {
      setSaving(true);
      
      const questionData = {
        ...formData,
        status: isDraft ? 'draft' : formData.status,
        options: formData.options.filter(opt => opt.trim()),
        topics: formData.topics.filter(topic => topic.trim()),
        tags: formData.tags.filter(tag => tag.trim()),
        hints: formData.hints.filter(hint => hint.trim()),
        references: formData.references.filter(ref => ref.trim())
      };

      await api.post('/questions', questionData);
      
      showNotification(
        `Question ${isDraft ? 'saved as draft' : 'created'} successfully`,
        'success'
      );
      
      navigate('/questions');
    } catch (err) {
      console.error('Save error:', err);
      showNotification(err.response?.data?.message || 'Failed to save question', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="create-question">
      {/* Header */}
      <div className="create-question__header">
        <div className="create-question__title">
          <h1>Create New Question</h1>
          <p>Build engaging questions with Expert Intelligence assistance</p>
        </div>
        
        <div className="create-question__actions">
          <Button
            variant="outline"
            icon={<FiEye />}
            onClick={() => setShowPreview(true)}
          >
            Preview
          </Button>
          
          <Button
            variant="outline"
            icon={<FiZap />}
            onClick={() => setShowAIAssistant(true)}
          >
            Expert Intelligence Assistant
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleSave(true)}
            loading={saving}
          >
            Save Draft
          </Button>
          
          <Button
            variant="primary"
            icon={<FiSave />}
            onClick={() => handleSave(false)}
            loading={saving}
          >
            Create Question
          </Button>
        </div>
      </div>

      <div className="create-question__content">
        <form className="create-question__form" onSubmit={(e) => e.preventDefault()}>
          {/* Basic Information */}
          <div className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  className={errors.subject ? 'error' : ''}
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="examType">Exam Type *</label>
                <select
                  id="examType"
                  value={formData.examType}
                  onChange={(e) => handleChange('examType', e.target.value)}
                  className={errors.examType ? 'error' : ''}
                >
                  <option value="">Select Exam Type</option>
                  {examTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.examType && <span className="error-message">{errors.examType}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="difficulty">Difficulty</label>
                <select
                  id="difficulty"
                  value={formData.difficulty}
                  onChange={(e) => handleChange('difficulty', e.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="form-section">
            <h2>Question Content</h2>
            
            <div className="form-group">
              <label htmlFor="questionText">Question Text *</label>
              <textarea
                id="questionText"
                value={formData.questionText}
                onChange={(e) => handleChange('questionText', e.target.value)}
                placeholder="Enter your question here..."
                rows={4}
                className={errors.questionText ? 'error' : ''}
              />
              {errors.questionText && <span className="error-message">{errors.questionText}</span>}
            </div>
            
            {/* Media Upload */}
            <div className="form-group">
              <label>Media (Optional)</label>
              <div className="media-upload">
                <input
                  type="file"
                  id="media-upload"
                  accept="image/*,video/*,audio/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outline"
                  icon={<FiUpload />}
                  onClick={() => document.getElementById('media-upload').click()}
                >
                  Upload Media
                </Button>
                
                {formData.media && (
                  <div className="media-preview">
                    <span>{formData.media.originalName}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<FiX />}
                      onClick={() => handleChange('media', null)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Answer Options */}
          <div className="form-section">
            <h2>Answer Options</h2>
            
            <div className="options-container">
              {formData.options.map((option, index) => (
                <div key={index} className="option-group">
                  <div className="option-header">
                    <label>
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={formData.correctAnswer === index}
                        onChange={() => handleChange('correctAnswer', index)}
                      />
                      Option {String.fromCharCode(65 + index)}
                    </label>
                    
                    {formData.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<FiMinus />}
                        onClick={() => removeOption(index)}
                      />
                    )}
                  </div>
                  
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Enter option ${String.fromCharCode(65 + index)}...`}
                  />
                </div>
              ))}
              
              {formData.options.length < 6 && (
                <Button
                  variant="outline"
                  icon={<FiPlus />}
                  onClick={addOption}
                  className="add-option-btn"
                >
                  Add Option
                </Button>
              )}
            </div>
            
            {errors.options && <span className="error-message">{errors.options}</span>}
            {errors.correctAnswer && <span className="error-message">{errors.correctAnswer}</span>}
          </div>

          {/* Explanation */}
          <div className="form-section">
            <h2>Explanation</h2>
            
            <div className="form-group">
              <label htmlFor="explanation">Answer Explanation</label>
              <textarea
                id="explanation"
                value={formData.explanation}
                onChange={(e) => handleChange('explanation', e.target.value)}
                placeholder="Explain why this is the correct answer..."
                rows={3}
              />
            </div>
          </div>

          {/* Topics and Tags */}
          <div className="form-section">
            <h2>Topics and Tags</h2>
            
            <div className="form-group">
              <label>Topics</label>
              <div className="topics-grid">
                {availableTopics.map(topic => (
                  <label key={topic} className="topic-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.topics.includes(topic)}
                      onChange={() => handleTopicToggle(topic)}
                    />
                    <span>{topic}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                placeholder="Type tags and press Enter or comma to add..."
                onKeyDown={handleTagInput}
              />
              
              {formData.tags.length > 0 && (
                <div className="tags-container">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                      >
                        <FiX size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="form-section">
            <h2>Advanced Settings</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="timeLimit">Time Limit (seconds)</label>
                <input
                  type="number"
                  id="timeLimit"
                  value={formData.timeLimit}
                  onChange={(e) => handleChange('timeLimit', parseInt(e.target.value))}
                  min="10"
                  max="600"
                  className={errors.timeLimit ? 'error' : ''}
                />
                {errors.timeLimit && <span className="error-message">{errors.timeLimit}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="points">Points</label>
                <input
                  type="number"
                  id="points"
                  value={formData.points}
                  onChange={(e) => handleChange('points', parseFloat(e.target.value))}
                  min="0.5"
                  max="10"
                  step="0.5"
                  className={errors.points ? 'error' : ''}
                />
                {errors.points && <span className="error-message">{errors.points}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
          </div>

          {/* Hints */}
          <div className="form-section">
            <h2>Hints (Optional)</h2>
            
            {formData.hints.map((hint, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={hint}
                  onChange={(e) => handleArrayChange('hints', index, e.target.value)}
                  placeholder={`Hint ${index + 1}...`}
                />
                
                {formData.hints.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<FiMinus />}
                    onClick={() => removeArrayItem('hints', index)}
                  />
                )}
              </div>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              icon={<FiPlus />}
              onClick={() => addArrayItem('hints')}
            >
              Add Hint
            </Button>
          </div>

          {/* References */}
          <div className="form-section">
            <h2>References (Optional)</h2>
            
            {formData.references.map((reference, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => handleArrayChange('references', index, e.target.value)}
                  placeholder={`Reference ${index + 1} (URL or citation)...`}
                />
                
                {formData.references.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<FiMinus />}
                    onClick={() => removeArrayItem('references', index)}
                  />
                )}
              </div>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              icon={<FiPlus />}
              onClick={() => addArrayItem('references')}
            >
              Add Reference
            </Button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Question Preview"
        size="large"
      >
        <div className="question-preview">
          <div className="preview-question">
            <h3>{formData.questionText || 'Question text will appear here...'}</h3>
            
            {formData.media && (
              <div className="preview-media">
                {formData.media.mimetype?.startsWith('image/') && (
                  <img src={formData.media.url} alt="Question media" />
                )}
                {formData.media.mimetype?.startsWith('video/') && (
                  <video controls src={formData.media.url} />
                )}
                {formData.media.mimetype?.startsWith('audio/') && (
                  <audio controls src={formData.media.url} />
                )}
              </div>
            )}
            
            <div className="preview-options">
              {formData.options.filter(opt => opt.trim()).map((option, index) => (
                <div
                  key={index}
                  className={`preview-option ${
                    formData.correctAnswer === index ? 'correct' : ''
                  }`}
                >
                  <span className="option-label">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span className="option-text">{option}</span>
                </div>
              ))}
            </div>
            
            {formData.explanation && (
              <div className="preview-explanation">
                <h4>Explanation:</h4>
                <p>{formData.explanation}</p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Expert Intelligence Assistant Modal */}
      <Modal
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        title="AI Question Assistant"
        size="large"
      >
        <div className="ai-assistant">
          <div className="ai-prompt">
            <label htmlFor="ai-prompt">Describe what you want to create:</label>
            <textarea
              id="ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g., Create a multiple choice question about photosynthesis for high school biology..."
              rows={3}
            />
            
            <Button
              variant="primary"
              icon={<FiZap />}
              onClick={generateAISuggestions}
              loading={aiGenerating}
              disabled={!aiPrompt.trim()}
            >
              Generate Suggestions
            </Button>
          </div>
          
          {aiSuggestions && (
            <div className="ai-suggestions">
              <h3>Expert Intelligence Suggestions</h3>
              {aiSuggestions.map((suggestion, index) => (
                <div key={index} className="ai-suggestion">
                  <div className="suggestion-content">
                    <h4>{suggestion.questionText}</h4>
                    <div className="suggestion-options">
                      {suggestion.options?.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          className={`suggestion-option ${
                            suggestion.correctAnswer === optIndex ? 'correct' : ''
                          }`}
                        >
                          {String.fromCharCode(65 + optIndex)}. {option}
                        </div>
                      ))}
                    </div>
                    {suggestion.explanation && (
                      <p className="suggestion-explanation">
                        <strong>Explanation:</strong> {suggestion.explanation}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => applyAISuggestion(suggestion)}
                  >
                    Use This
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CreateQuestion;